import fnum, { financial_sum, type FinancialNumber } from 'shared/number'

type HolsterOrAccessoryPricing = {
	base_price: string | FinancialNumber
	color_additional_price?: string | FinancialNumber | undefined
	promo_discount_rate?: undefined | string | FinancialNumber
	promo_discount_usd?: undefined | string | FinancialNumber
	promo_active?: boolean | undefined
}
type Accessory = HolsterOrAccessoryPricing & {
	holster_accessory_ref: number
	holster_accessory_colors: { color_ref: number, additional_price: string | FinancialNumber }[]
}
type ConfiguredAccessories = {
	valid_accessories_for_selection: Accessory[]
	selected_accessory_refs: Set<number>
	holster_accessory_ref_to_color_ref: Map<number, number>
	promo_active?: boolean | undefined
}
type ConfiguredHolster = ConfiguredAccessories & {
	base_price: string | FinancialNumber
	promo_discount_rate?: string | FinancialNumber
	color_additional_price: string | FinancialNumber
}

export const calculate_configured_price = ({ base_price, promo_discount_rate, color_additional_price, valid_accessories_for_selection, selected_accessory_refs, holster_accessory_ref_to_color_ref, promo_active }: ConfiguredHolster): FinancialNumber => {
	const colored_holster_price = calculate_holster_or_accessory_final_price({
		base_price,
		color_additional_price,
		promo_discount_rate,
		promo_active,
	})

	const colored_accessory_prices_sum = calculate_accessory_total({ valid_accessories_for_selection, selected_accessory_refs, holster_accessory_ref_to_color_ref, promo_active })

	return colored_holster_price.plus(colored_accessory_prices_sum)
}

const calculate_accessory_total = ({ valid_accessories_for_selection, selected_accessory_refs, holster_accessory_ref_to_color_ref, promo_active }: ConfiguredAccessories): FinancialNumber => financial_sum(
	valid_accessories_for_selection
		.filter(({ holster_accessory_ref }) => selected_accessory_refs.has(holster_accessory_ref))
		.map(({ base_price, holster_accessory_ref, holster_accessory_colors, promo_discount_rate, promo_discount_usd }) => {
			const selected_color_ref = holster_accessory_ref_to_color_ref.get(holster_accessory_ref)
			const color_additional_price = holster_accessory_colors.find(({ color_ref }) => color_ref === selected_color_ref)?.additional_price

			return calculate_holster_or_accessory_final_price({
				base_price,
				color_additional_price,
				promo_discount_rate,
				promo_discount_usd,
				promo_active,
			})
		})
)

export const calculate_holster_or_accessory_final_price = ({
	base_price,
	color_additional_price = `0`,
	promo_discount_rate,
	promo_discount_usd,
	promo_active,
}: HolsterOrAccessoryPricing): FinancialNumber => {
	const base_plus_color_price = fnum(base_price).plus(fnum(color_additional_price))
	const use_pct = promo_active && promo_discount_rate !== undefined && fnum(promo_discount_rate).gt(`0`)
	const use_fixed = promo_active && promo_discount_usd !== undefined && fnum(promo_discount_usd).gt(`0`)

	return use_pct
		? base_plus_color_price.minus(base_plus_color_price.times(promo_discount_rate))
		: use_fixed
			? base_plus_color_price.minus(promo_discount_usd)
			: base_plus_color_price
}
